import React, { useState } from 'react';
import { Modal, Upload, Button, Tabs, message, Typography, Space, Alert } from 'antd';
import { UploadOutlined, DownloadOutlined, InboxOutlined, FileExcelOutlined } from '@ant-design/icons';
import { ImportExportService } from '../services/importExport.service';
import { saveAs } from 'file-saver';

const { Dragger } = Upload;
const { TabPane } = Tabs;
const { Text, Title } = Typography;

interface ImportExportModalProps {
  open: boolean;
  onClose: () => void;
  estateId: string;
}

const ImportExportModal: React.FC<ImportExportModalProps> = ({ open, onClose, estateId }) => {
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      await ImportExportService.importData(estateId, file);
      message.success('Data imported successfully.');
      onClose();
    } catch (error: any) {
      message.error(error.message || 'Failed to import data.');
    } finally {
      setUploading(false);
    }
    return false; // Prevent default upload behavior
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await ImportExportService.exportData(estateId);
      saveAs(blob, `estate_${estateId}_export.xlsx`);
      message.success('Data exported successfully.');
    } catch (error: any) {
      message.error(error.message || 'Failed to export data.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title="Import / Export Data"
      width={600}
    >
      <Tabs defaultActiveKey="1">
        <TabPane tab="Import" key="1">
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Alert
              message="Import Instructions"
              description={
                <ul>
                  <li>File must be an Excel (.xlsx) file.</li>
                  <li>Required sheets: <b>Assets, Liabilities, Participants, Cashflows</b>.</li>
                  <li>Ensure numeric fields (Amount, Market Value) contain only numbers (no currency symbols or commas).</li>
                  <li>Dates should be in a valid format (e.g., YYYY-MM-DD).</li>
                </ul>
              }
              type="info"
              showIcon
            />
            <Text>
              <strong>Bulk Import Data</strong>
              <br />
              Upload an Excel file (.xlsx) containing multiple sheets (Assets, Liabilities, Participants, Cashflows) to mass import data into this estate.
              <br />
              Ensure the file follows the required structure with correct sheet names and headers.
            </Text>
            <Dragger
              name="file"
              multiple={false}
              accept=".xlsx"
              beforeUpload={handleUpload}
              showUploadList={false}
              disabled={uploading}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag file to this area to upload</p>
              <p className="ant-upload-hint">
                Support for a single upload. Strictly prohibit from uploading company data or other
                band files
              </p>
            </Dragger>
            {uploading && <Text type="secondary">Importing data... Please wait.</Text>}
          </Space>
        </TabPane>
        <TabPane tab="Export / Template" key="2">
          <Space direction="vertical" style={{ width: '100%', alignItems: 'center', padding: '20px 0' }} size="large">
            <Text>
              Download a full report with both your raw inputs and all computed final values.
              <br />
              You can also use this exported file as a <strong>template</strong> for importing data.
            </Text>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              size="large"
              onClick={handleExport}
              loading={exporting}
            >
              Download Excel Report / Template
            </Button>
          </Space>
        </TabPane>
      </Tabs>
    </Modal>
  );
};

export default ImportExportModal;
